import React, { useLayoutEffect, useRef, useState } from 'react';

import { ISingleEventStage } from '../types';
import { ProgressBar } from './ProgressBar';
import { StageListItem } from './StageListItem';

type ComponentVariant = 'singleEvent' | 'referral';

type StageListProps = {
  stages: ISingleEventStage[];
  componentVariant?: ComponentVariant;
};

type StageVariant = 'past' | 'current' | 'future';

type StagePosition = {
  left: number;
  width: number;
  center: number;
};

export const StageList = ({ stages, componentVariant = 'singleEvent' }: StageListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [stagePositions, setStagePositions] = useState<StagePosition[]>([]);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);

  const getStageVariant = (stageIndex: number): StageVariant => {
    const currentTime = Date.now() / 1000;
    const sortedStages = [...stages].sort((a, b) => a.timestamp - b.timestamp);

    // Find the last stage that has already started
    let currentStageIndex = -1;
    for (let i = 0; i < sortedStages.length; i++) {
      if (sortedStages[i].timestamp <= currentTime) {
        currentStageIndex = i;
      } else {
        break;
      }
    }

    const currentStage = stages[stageIndex];
    const currentStageInSorted = sortedStages.findIndex((s) => s.id === currentStage.id);

    if (currentStageInSorted < currentStageIndex) {
      return 'past';
    } else if (currentStageInSorted === currentStageIndex && currentStageIndex >= 0) {
      return 'current';
    } else {
      return 'future';
    }
  };

  const getCurrentStageIndex = (): number => {
    const currentTime = Date.now() / 1000;
    const sortedStages = [...stages].sort((a, b) => a.timestamp - b.timestamp);

    let currentStageIndex = 0;
    for (let i = 0; i < sortedStages.length; i++) {
      if (sortedStages[i].timestamp <= currentTime) {
        currentStageIndex = i;
      } else {
        break;
      }
    }

    return currentStageIndex;
  };

  useLayoutEffect(() => {
    const measurePositions = () => {
      if (!containerRef.current || stageRefs.current.length === 0) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const positions: StagePosition[] = [];

      stageRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const left = rect.left - containerRect.left + containerRef.current!.scrollLeft;
          const width = rect.width;
          const center = left + width / 2;

          positions.push({ left, width, center });
        }
      });

      setStagePositions(positions);
      setContainerWidth(containerRect.width);
    };

    const handleScroll = () => {
      if (containerRef.current) {
        setScrollLeft(containerRef.current.scrollLeft);
      }
    };

    measurePositions();

    // Add scroll listener
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    // Re-measure on window resize
    window.addEventListener('resize', measurePositions);

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', measurePositions);
    };
  }, [stages]);

  // Adjust positions to account for scroll offset
  const adjustedPositions = stagePositions.map((pos) => ({
    ...pos,
    left: pos.left - scrollLeft,
    center: pos.center - scrollLeft,
  }));

  return (
    <div className="tw-relative">
      <div
        ref={containerRef}
        className="tw-scrollbar-hide tw-flex tw-flex-row tw-gap-4 tw-overflow-x-auto lg:tw-overflow-x-visible"
      >
        <div className="tw-w-4 tw-flex-shrink-0 lg:tw-hidden" />
        {stages.map((stage, index) => (
          <div
            key={stage.id}
            ref={(el) => {
              stageRefs.current[index] = el;
            }}
          >
            <StageListItem {...stage} variant={getStageVariant(index)} componentVariant={componentVariant} />
          </div>
        ))}
        <div className="tw-w-4 tw-flex-shrink-0 lg:tw-hidden" />
      </div>
      <div className="tw-h-[24px] tw-w-full" />
      {adjustedPositions.length > 0 && containerWidth > 0 && (
        <ProgressBar
          stagePositions={adjustedPositions}
          currentStageIndex={getCurrentStageIndex()}
          containerWidth={containerWidth}
          componentVariant={componentVariant}
        />
      )}
    </div>
  );
};
